import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import i18n from '../../i18n';
import initializeEcho from '../../utils/echo';
import api from '../../utils/api';
import { LayoutDashboard, Building2, Wrench, CarFront, GalleryVertical, UsersRound, FolderCog2,CalendarCog } from "lucide-react"

export default function DashboardLayout({ children }: { children: React.ReactNode }) {
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [profileMenuVisible, setProfileMenuVisible] = useState(false);
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [pendingOrders, setPendingOrders] = useState(0);
  const [pendingReservations, setPendingReservations] = useState(0);
  const [pendingEnqueries, setEnqueries] = useState(0);
    const [echo, setEcho] = useState<any>(null);
  
  const getOrders = ()=>{return pendingOrders;} 
  const getReservations = ()=>{return pendingReservations;} 
  const getEnqueries = ()=>{return pendingEnqueries;} 

  const menuItems = [
    { label: t('dashboard'), icon: <LayoutDashboard className='mr-2' size={24} />, path: '/' },
    { label: t('companies'), icon: <Building2 className='mr-2'  size={24} />, path: '/companies'  , 'has_value' : getEnqueries },
    { label: t('services'), icon: <Wrench className='mr-2'  size={24} />, path: '/services' },
    { label: t('brands'), icon: <CarFront className='mr-2'  size={24} />, path: '/brands' },
    { label: t('orders'), icon: <FolderCog2 className='mr-2'  size={24} />, path: '/orders' , 'has_value' : getOrders },
    { label: t('banners'), icon: <GalleryVertical className='mr-2'  size={24} />, path: '/banners' },
    { label: t('technicians'), icon: <UsersRound className='mr-2'  size={24} />, path: '/technicians' },
    { label: t('agents'), icon: <UsersRound className='mr-2'  size={24} />, path: '/agents' },
    { label: t('reservations'), icon: <CalendarCog className='mr-2'  size={24} />, path: '/reservations' , 'has_value' : getReservations },
    { label: t('banners_website'), icon: <GalleryVertical className='mr-2'  size={24} />, path: '/banners/website' },

  ];
  const languages = [
   // { label: 'English', value: 'en' },
    { label: 'Français', value: 'fr' },
    //{ label: 'العربية', value: 'ar' },
  ];

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('userLanguage', lng);
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setSidebarVisible(true);
      }
    };
    
    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!echo) {
    fetchPendingOrders();
    fetchPendingEnqueries();
    fetchPendingReservations();
    const setupEcho = async () => {
      const echoInstance = await initializeEcho();
      setEcho(echoInstance);
    };
    setupEcho();
  }
    return () => {
      if (echo) {
    //    echo.disconnect();
      }
    };
  }, [echo]);


  useEffect(() => {
    if (echo) {
      const channelName = `dashboard_notification`;
      const channel = echo.private(channelName);
      
      const newOrderHandler = async (data) => {
        await fetchPendingOrders();
        if ("Notification" in window) {
          Notification.requestPermission().then(permission => {
            if (permission === "granted") {
              const notif = new Notification(t('notif_new_order'), {
                body: t('notif_new_order_body'),
                icon: "/icon.png"  
              });
      
              notif.onclick = () => {
                window.focus();
                navigate('/orders');
              };
            }
          });
        }
      };
      
      const newReservationHandler = async (data) => {
        await fetchPendingReservations();
        if ("Notification" in window) {
          Notification.requestPermission().then(permission => {
            if (permission === "granted") {
              const notif = new Notification(t('notif_new_reservation'), {
                body: t('notif_new_reservation_body'),
                icon: "/icon.png"  
              });
      
              notif.onclick = () => {
                window.focus();
                navigate('/reservations');
              };
            }
          });
        }
      };
      
      const newMessageHandler = async (data) => {
        fetchPendingOrders();
        if ("Notification" in window) {
          Notification.requestPermission().then(permission => {
            if (permission === "granted") {
              const notif = new Notification(t('notif_new_msg'), {
                body: t('notif_new_msg_body'),
                icon: "/icon.png" 
              });
      
              notif.onclick = () => {
                window.focus();
                navigate('/orders/'+data?.id+"?open_chat=true");
              };
            }
          });
        }
      };
      
      const generalNotifHandler = async (data) => {
        if ("Notification" in window) {
          Notification.requestPermission().then(permission => {
            if (permission === "granted") {
              const notif = new Notification(t(data?.title || 'notif_title'), {
                body: t(data?.body || 'notif_body'),
                icon: "/icon.png" 
              });
              notif.onclick = () => {
                if(data?.type=='enquery'){
                  window.focus();
                  navigate('/companies');
                }
              };
              if(data?.type=='enquery'){
                fetchPendingEnqueries();
              }
            }
          });
        }
      };
  
      // Register listeners
      channel.listen('.new.order.notif', newOrderHandler);
      channel.listen('.new.reservation.notif', newReservationHandler);
      channel.listen('.new.msg.notif', newMessageHandler);
      channel.listen('.new.general.notif', generalNotifHandler);
  
      // Cleanup function
      return () => {
        channel.stopListening('.new.order.notif', newOrderHandler);
        channel.stopListening('.new.reservation.notif', newReservationHandler);
        channel.stopListening('.new.msg.notif', newMessageHandler);
        channel.stopListening('.new.general.notif', generalNotifHandler);
      };
    }
  }, [echo, navigate, t]);

 
  const fetchPendingOrders = async () => {

    try {
      const response = await api.get('service-orders/pending/count');
      setPendingOrders(response.data);
    } catch (error) {
      console.error('Error fetching pending orders:', error);
    }
  };
  const fetchPendingReservations = async () => {

    try {
      const response = await api.get('service-reservations/pending/count');
      setPendingReservations(response.data);
    } catch (error) {
      console.error('Error fetching pending orders:', error);
    }
  };
  const fetchPendingEnqueries = async () => {

    try {
      const response = await api.get('companies/enqueries/count');
      setEnqueries(response.data);
    } catch (error) {
      console.error('Error fetching pending orders:', error);
    }
  };

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">
      {/* Sidebar */}
      <aside 
        className={`
          bg-white w-64 min-h-screen p-4 shadow-lg transform transition-transform duration-300 ease-in-out
          ${sidebarVisible ? 'translate-x-0' : '-translate-x-full'}
          md:relative md:translate-x-0
        `}
      >
        <div className="flex items-center justify-between mb-8">
          <img   src="/images/logo.png" />
          <button 
            onClick={() => setSidebarVisible(false)}
            className="md:hidden text-gray-500 hover:text-gray-700 focus:outline-none"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
        <nav>
          <ul className="space-y-2">
            {menuItems.map((item) => (
              <li key={item.path}>
                <a
                  href={item.path}
                  className={`
                    flex items-center  flex-1 p-3 space-between rounded-lg transition-colors duration-200
                    ${location.pathname === item.path 
                      ? 'bg-blue-100 text-blue-600' 
                      : 'text-gray-700 hover:bg-gray-100'
                    }
                  `}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(item.path);
                    if (window.innerWidth < 768) {
                      setSidebarVisible(false);
                    }
                  }}
                >
                 {item.icon}
                  {item.label}
                  {item.has_value &&
                    <div className='w-full flex-1 flex-grow flex-row items-end flex'>
                      <span className=' rounded-full bg-[#a6111f] ml-auto  px-2 text-white'>{item.has_value()}</span></div>
                  }
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      {/* Main content */}
      <div className="flex flex-col flex-grow overflow-hidden">
        {/* Header */}
        <header className="bg-white shadow-md z-2">
          <div className="flex justify-between items-center px-4 py-3">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarVisible(!sidebarVisible)}
                className="md:hidden p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
              >
                <i className={`fas fa-${sidebarVisible ? 'times' : 'bars'}`}></i>
              </button>
              <h1 className="text-xl font-semibold text-gray-800 capitalize ml-4">
                {t(location.pathname.substring(1) || 'dashboard')}
              </h1>
            </div>
            <div className="flex items-center space-x-4 flex-fit">
  {/*             <select
                value={i18n.language}
                onChange={(e) => changeLanguage(e.target.value)}
                className="w-32 p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
              >
                {languages.map((lang) => (
                  <option key={lang.value} value={lang.value}>
                    {lang.label}
                  </option>
                ))}
              </select> */}
              <div className="relative flex-fit">
                <button
                  className="flex items-center space-x-2 focus:outline-none"
                  onClick={() => setProfileMenuVisible(!profileMenuVisible)}
                >{
                  !user?.avatar ? 
             <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-gray-600 text-l ">
                {user.first_name[0]}{user.last_name[0]}
              </div>
              :
                  <img
                    src={user?.avatar }
                    alt="User avatar"
                    className="w-8 h-8 rounded-full border-2 border-blue-500"
                  />}
                  <span className="font-medium text-gray-700">{user?.first_name} {user?.last_name}</span>
                  <i className={`fas fa-chevron-down text-gray-500 transition-transform duration-200 ${profileMenuVisible ? 'rotate-180' : ''}`}></i>
                </button>
                {profileMenuVisible && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10">
                    <a href="/profile" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">{t('profile')}</a>
                    <button onClick={logout} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">{t('logout')}</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Main content */}
        <main className="flex-grow overflow-auto p-6">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

